import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { fmtNumber } from '@/shared/lib/format'
import { getPaceReport, listPaceChecks, takePaceCheck, type PaceReport, type PaceStation } from './api'
import { ElectionPicker } from './ElectionPicker'
import { useElection } from './useElection'

/**
 * Turnout pace — "who is behind, and who do I call".
 *
 * ## The one sentence this page exists to say
 *
 * *The field is at 54%. These eight stations are below 35%. Here are their cells and the
 * person who can move each one.* Everything on the screen is in service of that sentence,
 * and anything that would need explaining before the sentence makes sense is not here.
 *
 * ## Why there is no "mark as voted" button anywhere on it
 *
 * A cell's turnout is live data reported from the field by the mobilizer who owns the cell.
 * This page names that person and gives their masked contact, and that is the end of what
 * it can do. A console that let somebody correct a cell's numbers from a desk would produce
 * turnout figures indistinguishable afterwards from the ones a person walked to a polling
 * station to collect — and on the day the two disagree, nobody could tell which was which.
 *
 * ## The comparison is the field, and the page says so
 *
 * Nothing here is measured against an expected curve. A station is behind when it is below
 * the rest of the campaign at the same minute — so when the whole field is slow, nobody is
 * singled out for it and the page prints the clock instead, leaving the reader to draw the
 * conclusion a curve would have drawn for them wrongly.
 */
export function PacePage() {
  const canRead = useCan('turnout:analyse')
  const selection = useElection()
  const { electionId } = selection
  const [params, setParams] = useSearchParams()
  const requested = params.get('check')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const checks = useLoad(
    () => (electionId ? listPaceChecks(electionId) : Promise.resolve([])), [electionId])

  const list = checks.data ?? []
  // The newest checkpoint unless a link named one — an emailed report is a link to the
  // report it was about, not to whatever has happened since.
  const selected = (requested && list.find((c) => c.id === requested)) || list[0] || null

  const report = useLoad(
    () => (selected ? getPaceReport(selected.id) : Promise.resolve(null)), [selected?.id])

  async function take() {
    if (!electionId) return
    setBusy(true)
    setError(null)
    try {
      const taken = await takePaceCheck(electionId)
      await checks.reload()
      setParams((p) => {
        const next = new URLSearchParams(p)
        next.set('check', taken.id)
        return next
      }, { replace: true })
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  if (!canRead) return <Forbidden what="Turnout pace" />

  const data = report.data

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Who is behind"
        description="Taken at set times on polling day and kept, so the 11:00 report still says
                     what it said at 11:00. A station is behind when it is below the rest of the
                     campaign at the same minute — not below a forecast."
        right={(
          <div className="flex items-center gap-2">
            <ElectionPicker selection={selection} />
            <button type="button" className="erp-btn-secondary h-9 text-erp-sm"
                    disabled={busy || !electionId} onClick={take}>
              {busy ? 'Taking…' : 'Take a reading now'}
            </button>
          </div>
        )}
      />

      <ErrorStrip message={error ?? checks.error ?? report.error} />

      {list.length > 1 && (
        <div className="flex flex-wrap items-center gap-1" role="tablist">
          {list.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={c.id === selected?.id}
              onClick={() => setParams((p) => {
                const next = new URLSearchParams(p)
                next.set('check', c.id)
                return next
              }, { replace: true })}
              className={
                'rounded border px-2 py-1 text-erp-xs '
                + (c.id === selected?.id
                  ? 'border-foreground bg-muted font-medium'
                  : 'border-border text-muted-foreground hover:bg-muted/50')
              }
            >
              {c.checkpointCode}
              <span className="ml-1.5 tabular-nums">
                {c.stationsBehind === 0 ? 'all keeping up' : `${c.stationsBehind} behind`}
              </span>
            </button>
          ))}
        </div>
      )}

      {checks.loading && list.length === 0 ? (
        <LoadingBlock label="Loading the readings…" />
      ) : !selected ? (
        <Panel title="No reading has been taken yet">
          <p className="p-3 text-erp-sm text-muted-foreground">
            Readings are taken automatically on polling day, at the times set under
            Settings → Turnout. Outside polling day there is nothing to measure — press
            <b> Take a reading now</b> to run one against whatever has been reported so far.
          </p>
        </Panel>
      ) : !data ? (
        <LoadingBlock label="Loading the report…" />
      ) : (
        <Report report={data} />
      )}
    </div>
  )
}

function Report({ report }: { report: PaceReport }) {
  const pct = (share: number) => `${Math.round(share * 100)}%`

  return (
    <div className="space-y-4">
      {/* The headline, in words, before any table. */}
      <Panel>
        <div className="p-3">
          <p className="text-erp-base">
            {report.stationsBehind === 0 ? (
              <>
                At <b>{report.checkpointCode}</b>, <b>{pct(report.fieldVotedShare)}</b> of the
                people in our cells had voted, and <b>no station was far enough below that to
                be worth a call</b>.
              </>
            ) : (
              <>
                At <b>{report.checkpointCode}</b>, <b>{pct(report.fieldVotedShare)}</b> of the
                people in our cells had voted. <b>{fmtNumber(report.stationsBehind)}{' '}
                {report.stationsBehind === 1 ? 'station is' : 'stations are'} well below
                that</b>, covering {fmtNumber(report.cellsBehind)} cells.
              </>
            )}
          </p>
          <p className="mt-1.5 text-erp-xs text-muted-foreground">
            {Math.round(report.windowElapsedPct)}% of the polling day had gone.{' '}
            {report.stationsBehind === 0
              ? 'That is a statement about the spread between stations, not about whether '
                + 'the overall number is good — if the whole field is slow, this page will '
                + 'not single anybody out for it.'
              : `A station is listed when it is below ${Math.round(report.behindRatio * 100)}% `
                + `of the field AND at least ${report.minGapPp} points behind it. Stations `
                + `with fewer than ${report.minMembers} active members are not ranked.`}
          </p>
          {report.notifyError ? (
            <p className="mt-2 text-erp-xs text-destructive">
              This report was not sent: {report.notifyError}
            </p>
          ) : report.notifiedAt ? (
            /* "Accepted for delivery", not "delivered" — the two are different claims and
               the notification port is explicit about it. Whether it arrived is the
               Notification Log's answer, not this page's. */
            <p className="mt-2 text-erp-xs text-muted-foreground">
              Handed to the mail gateway at {new Date(report.notifiedAt).toLocaleTimeString()}
              {' '}— see the Notification Log for whether it arrived.
            </p>
          ) : null}
        </div>
      </Panel>

      <KpiStrip>
        <MetricCard label="The field" value={pct(report.fieldVotedShare)}
          sub={`${fmtNumber(report.votedCount)} of ${fmtNumber(report.membersActive)} have voted`} />
        <MetricCard label="Stations behind" value={fmtNumber(report.stationsBehind)}
          sub={`of ${fmtNumber(report.stationsTotal)} with cells`}
          accent={report.stationsBehind > 0} />
        <MetricCard label="Cells behind" value={fmtNumber(report.cellsBehind)}
          sub="inside those stations" accent={report.cellsBehind > 0} />
        <MetricCard label="Day gone" value={`${Math.round(report.windowElapsedPct)}%`}
          sub="of the polling window" />
      </KpiStrip>

      {report.stations.length > 0 && report.stations.length < report.stationsBehind ? (
        <p className="text-erp-xs text-muted-foreground">
          Showing the worst {report.stations.length} of {fmtNumber(report.stationsBehind)}.
          A list nobody can work through before the polls close is not a call list.
        </p>
      ) : null}

      {report.stations.length === 0 ? (
        <Panel title="Nobody to call">
          <p className="p-3 text-erp-sm text-muted-foreground">
            Every station with enough members to rank is keeping up with the field.
          </p>
        </Panel>
      ) : (
        report.stations.map((station) => <Station key={station.id} station={station} />)
      )}
    </div>
  )
}

/**
 * One station, with EVERY cell under it — not only the slow ones.
 *
 * The cells that are keeping up are the evidence that the station's problem is specific
 * rather than general. "Four of your nine cells have not moved" is a different instruction
 * from "your station is behind", and a list of four cells does not say it.
 */
function Station({ station }: { station: PaceStation }) {
  const pct = (share: number) => `${Math.round(share * 100)}%`
  const far = station.band === 'FAR_BEHIND'

  return (
    <Panel
      title={station.stationName}
      description={`${pct(station.votedShare)} — ${fmtNumber(station.votedCount)} of `
        + `${fmtNumber(station.membersActive)} have voted, `
        + `${Math.round(station.shortfallPp)} points below the field. `
        + `${station.cellsBehind} of ${station.cellsTotal} cells behind.`}
      right={(
        <span className={far ? 'text-destructive' : 'text-amber-600 dark:text-amber-500'}>
          {far ? 'Far behind' : 'Behind'}
        </span>
      )}
    >
      <ErpTable head={(
        <>
          <Th>Cell</Th>
          <Th className="text-right">Voted</Th>
          <Th className="text-right">Of</Th>
          <Th className="text-right">Share</Th>
          <Th>Coordinator</Th>
          <Th>Mobilizer — the only person who can update this</Th>
        </>
      )}>
        {station.cells.length === 0 ? (
          <EmptyRow colSpan={6} message="No cells recorded under this station." />
        ) : station.cells.map((cell) => (
          <tr key={cell.id} className={cell.behind ? 'bg-destructive/5' : undefined}>
            <Td>
              <span className="font-medium">{cell.cellCode}</span>
              {cell.cellLabel ? (
                <span className="ml-1.5 text-erp-xs text-muted-foreground">
                  {cell.cellLabel}
                </span>
              ) : null}
            </Td>
            <Td className="text-right tabular-nums">{cell.votedCount}</Td>
            <Td className="text-right tabular-nums">{cell.membersActive}</Td>
            <Td className="text-right tabular-nums">
              {cell.membersActive === 0 ? '—' : pct(cell.votedShare)}
            </Td>
            <Td>{cell.coordinatorName ?? <span className="text-muted-foreground">—</span>}</Td>
            <Td>
              {cell.mobilizerName ?? <span className="text-muted-foreground">nobody assigned</span>}
              {cell.contactMask ? (
                <span className="ml-1.5 text-erp-xs text-muted-foreground tabular-nums">
                  {cell.contactMask}
                </span>
              ) : null}
            </Td>
          </tr>
        ))}
      </ErpTable>
    </Panel>
  )
}
